/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './UserManagement.css';

// --- SVG Icons ---
const Icons = {
    Key: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path></svg>,
    Trash: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>,
    User: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>,
    AddUser: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><line x1="20" y1="8" x2="20" y2="14"></line><line x1="23" y1="11" x2="17" y2="11"></line></svg>
};

export default function UserManagement({ currentUser }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newUser, setNewUser] = useState({ username: '', password: '', confirmPassword: '' });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => { loadUsers(); }, []);

    const loadUsers = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/get_users.php`);
            if (response.data.success) setUsers(response.data.users || []);
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');

        if (!newUser.username || !newUser.password || !newUser.confirmPassword) return setError('All fields required');
        if (newUser.username.length < 3) return setError('Username too short (min 3)');
        if (newUser.password.length < 6) return setError('Password too short (min 6)');
        if (newUser.password !== newUser.confirmPassword) return setError('Passwords do not match');

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/add_user.php`, {
                username: newUser.username,
                password: newUser.password
            });

            if (response.data.success) {
                setSuccess('User added successfully!');
                setNewUser({ username: '', password: '', confirmPassword: '' });
                loadUsers();
            } else {
                setError(response.data.error || 'Failed to add user');
            }
        } catch (error) {
            setError('Failed to add user: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleDeleteUser = async (userId, username) => {
        if (username === 'admin') return alert('Cannot delete admin!');
        if (!window.confirm(`Delete user "${username}"?`)) return;

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/delete_user.php`, { user_id: userId });
            if (response.data.success) {
                setSuccess('User deleted!');
                loadUsers();
            } else {
                setError(response.data.error || 'Failed to delete');
            }
        } catch (error) { setError('Delete failed'); }
    };

    const handleChangePassword = async (userId, username) => {
        const newPassword = prompt(`New password for "${username}" (min 6 chars):`);
        if (!newPassword) return;
        if (newPassword.length < 6) return alert('Password too short');

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/change_password.php`, {
                user_id: userId, new_password: newPassword
            });
            if (response.data.success) alert('Password changed!');
            else alert(response.data.error || 'Failed');
        } catch (error) { alert('Change failed'); }
    };

    const handleChangeRole = async (userId, username, currentRole) => {
        if (currentRole === 'developer') return alert('Cannot change developer role.');
        const allowedRoles = ['teacher', 'admin', 'hod'];

        const newRole = prompt(`Enter new role for "${username}" (${allowedRoles.join(', ')}):`, currentRole);
        if (!newRole) return;

        const trimmedRole = newRole.trim().toLowerCase();
        if (!allowedRoles.includes(trimmedRole)) return alert(`Invalid role. Allowed: ${allowedRoles.join(', ')}`);

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/update_user_role.php`, {
                user_id: userId, role: trimmedRole
            });
            if (response.data.success) {
                alert('Role updated!');
                loadUsers();
            } else {
                alert(response.data.error || 'Update failed');
            }
        } catch (error) { alert('Update failed'); }
    };

    return (
        <div className="users-page-wrapper">

            {/* Header */}
            <div className="page-header">
                <div>
                    <h1>User Management</h1>
                    <p>Manage system access and permissions.</p>
                </div>
            </div>

            {/* Add User Card */}
            {currentUser?.role === 'developer' && (
                <div className="modern-card form-card">
                    <div className="card-header-row">
                        <h3><Icons.AddUser /> Add New User</h3>
                    </div>

                    {error && <div className="alert error">{error}</div>}
                    {success && <div className="alert success">{success}</div>}

                    <form onSubmit={handleAddUser} className="user-form">
                        <div className="form-group">
                            <label>Username</label>
                            <input
                                className="modern-input"
                                type="text"
                                value={newUser.username}
                                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                                placeholder="Username"
                            />
                        </div>
                        <div className="form-group">
                            <label>Password</label>
                            <input
                                className="modern-input"
                                type="password"
                                value={newUser.password}
                                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                placeholder="Password"
                            />
                        </div>
                        <div className="form-group">
                            <label>Confirm</label>
                            <input
                                className="modern-input"
                                type="password"
                                value={newUser.confirmPassword}
                                onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                                placeholder="Confirm"
                            />
                        </div>
                        <button type="submit" className="btn-primary full-width" style={{ marginTop: '24px' }}>
                            Create User
                        </button>
                    </form>
                </div>
            )}

            {/* User List */}
            <div className="table-container">
                {loading ? (
                    <div className="loading-state">Loading users...</div>
                ) : (
                    <table className="modern-table">
                        <thead>
                            <tr>
                                <th>Username</th>
                                <th>Role</th>
                                <th>Created</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.length > 0 ? (
                                users.map((user) => (
                                    <tr key={user.user_id}>
                                        <td style={{ fontWeight: '600', color: 'var(--text-dark)' }}>
                                            {user.username}
                                            {user.role === 'admin' && <span className="badge admin">ADMIN</span>}
                                        </td>
                                        <td>
                                            <span className={`role-pill ${user.role}`}>
                                                {user.role.toUpperCase()}
                                            </span>
                                        </td>
                                        <td style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>
                                            {new Date(user.created_at).toLocaleDateString()}
                                        </td>
                                        <td>
                                            <div className="actions-cell">
                                                <button
                                                    className="icon-btn edit"
                                                    onClick={() => handleChangeRole(user.user_id, user.username, user.role)}
                                                    disabled={user.role === 'developer'}
                                                    title="Change Role"
                                                >
                                                    <Icons.User />
                                                </button>
                                                <button
                                                    className="icon-btn key"
                                                    onClick={() => handleChangePassword(user.user_id, user.username)}
                                                    title="Change Password"
                                                >
                                                    <Icons.Key />
                                                </button>
                                                {user.username !== 'admin' && (
                                                    <button
                                                        className="icon-btn delete"
                                                        onClick={() => handleDeleteUser(user.user_id, user.username)}
                                                        title="Delete User"
                                                    >
                                                        <Icons.Trash />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                        No users found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}